import { useEffect, useMemo, useState } from "react";
import { useDebounce } from "../hooks";

export interface UsePaginator {
  pageSizeOptions: { label: string; value: number }[];
  setPageSize: (size: number) => void;
  start: number;
  end: number;
  canInc: boolean;
  canDec: boolean;
  inc: () => void;
  dec: () => void;
  pageOption: { label: string; value: number } | undefined;
}

type Props = {
  page: number;
  pageSize?: number;
  pageSizes?: number[];
  count: number;
  onChange: ({ page, pageSize }: { page: number; pageSize: number }) => void;
};

const DEFAULT_PAGE_SIZES = [50, 100];

export const usePaginator = (props: Props): UsePaginator => {
  const { pageSizes, count, onChange } = props;

  const pageSizeOptions = useMemo(
    () =>
      (pageSizes ?? DEFAULT_PAGE_SIZES).map((i) => ({
        value: i,
        label: i.toString(),
      })),
    [pageSizes]
  );

  const [pageSize, setPageSize] = useState<number>(props.pageSize ?? 25);
  const [page, setPage] = useState(props.page ?? 0);

  const inc = () => setPage((curr) => curr + 1);
  const dec = () => setPage((curr) => curr - 1);

  const onChangeDebounced = useDebounce(onChange, 100);

  const pageOption = useMemo(
    () =>
      pageSizeOptions.find((option) => option.value === pageSize) ??
      pageSizeOptions[0],
    [pageSizeOptions, pageSize]
  );

  const start = page * pageSize + 1;
  const end = Math.min((page + 1) * pageSize, count);
  const canInc = (page + 1) * pageSize < count;
  const canDec = page > 0;

  useEffect(() => {
    setPage(0);
  }, [pageSize]);

  useEffect(() => {
    onChangeDebounced({ page, pageSize });
  }, [page, pageSize, onChangeDebounced]);

  return {
    pageSizeOptions,
    pageOption,
    start,
    end,
    canInc,
    canDec,
    setPageSize,
    inc,
    dec,
  };
};
